/**
 * Calculate settlement range based on accident details
 * 
 * Base Injury Bands:
 * - No injury / soreness → $0 – $8,000
 * - Soft tissue → $10,000 – $45,000
 * - Fracture → $40,000 – $120,000
 * - Surgery → $90,000 – $300,000
 * - Permanent injury → $250,000 – $1,000,000+
 * 
 * Adjusters:
 * - ER visit: +10–20%
 * - Missed work 1+ month: +15–30%
 * - Clear fault + police report: +20–40%
 * - Commercial vehicle: +30–60%
 */

// Base injury bands
const injuryBands = {
  none: { min: 0, max: 8000 },
  softTissue: { min: 10000, max: 45000 },
  fracture: { min: 40000, max: 120000 },
  surgery: { min: 90000, max: 300000 },
  permanent: { min: 250000, max: 1000000 },
};

// Adjuster ranges (min and max percentage increases)
const adjusters = {
  erVisit: { min: 0.10, max: 0.20 },
  missedWorkMonth: { min: 0.15, max: 0.30 },
  clearFaultWithReport: { min: 0.20, max: 0.40 },
  commercialVehicle: { min: 0.30, max: 0.60 },
};

/**
 * Calculate the settlement range based on answers
 * @param {Object} answers - The user's answers to the estimator questions
 * @returns {Object} - { min: number, max: number, formatted: { min: string, max: string } }
 */
export function calculateSettlement(answers) {
  const {
    accidentType,
    erVisit,
    injurySeverity,
    missedWork,
    fault,
    policeReport,
  } = answers;

  // Get base band from injury severity
  const baseBand = injuryBands[injurySeverity] || injuryBands.none;
  let minValue = baseBand.min;
  let maxValue = baseBand.max;

  // Apply adjusters silently
  
  // ER visit adjuster (+10-20%)
  if (erVisit === 'yes') {
    minValue *= (1 + adjusters.erVisit.min);
    maxValue *= (1 + adjusters.erVisit.max);
  }

  // Missed work 1+ month adjuster (+15-30%)
  if (missedWork === 'months') {
    minValue *= (1 + adjusters.missedWorkMonth.min);
    maxValue *= (1 + adjusters.missedWorkMonth.max);
  }

  // Clear fault + police report adjuster (+20-40%)
  if (fault === 'yes' && policeReport === 'yes') {
    minValue *= (1 + adjusters.clearFaultWithReport.min);
    maxValue *= (1 + adjusters.clearFaultWithReport.max);
  }

  // Commercial vehicle adjuster (+30-60%)
  if (accidentType === 'truck') {
    minValue *= (1 + adjusters.commercialVehicle.min);
    maxValue *= (1 + adjusters.commercialVehicle.max);
  }

  // Round to nice numbers for display
  minValue = roundToNiceNumber(minValue);
  maxValue = roundToNiceNumber(maxValue);

  // Ensure min is less than max
  if (minValue >= maxValue) {
    maxValue = minValue * 1.5;
  }

  return {
    min: minValue,
    max: maxValue,
    formatted: {
      min: formatCurrency(minValue),
      max: formatCurrency(maxValue),
    },
  };
}

/**
 * Round to a "nice" number for display (nearest 1000 for small, 5000 for large)
 */
function roundToNiceNumber(value) {
  if (value < 10000) {
    return Math.round(value / 1000) * 1000;
  } else if (value < 100000) {
    return Math.round(value / 5000) * 5000;
  } else {
    return Math.round(value / 10000) * 10000;
  }
}

/**
 * Format number as currency
 */
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default calculateSettlement;


